import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Wrench, Trash2, ShieldCheck, Calendar as CalIcon, Package, CreditCard } from "lucide-react";
import { Sensitive } from "@/components/security/Sensitive";

const fmtMoney = (n, ccy = "INR") =>
    n == null ? "—" : `${ccy === "INR" ? "₹" : ccy + " "}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const InfoRow = ({ label, value }) => (
    <div className="py-2 border-b border-stone-100 last:border-0">
        <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
        <div className="text-sm text-stone-900 mt-0.5">{value == null || value === "" ? "—" : value}</div>
    </div>
);

export default function AssetDetail() {
    const { id } = useParams();
    const [asset, setAsset] = useState(null);
    const [related, setRelated] = useState(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [f, setF] = useState({ date: new Date().toISOString().slice(0, 10), vendor: "", cost: "", notes: "" });

    const load = useCallback(async () => {
        try {
            const [a, r] = await Promise.all([
                api.get(`/assets/${id}`),
                api.get(`/assets/${id}/related`),
            ]);
            setAsset(a.data);
            setRelated(r.data);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    const remove = async () => {
        if (!window.confirm("Archive this asset? Its history will be preserved.")) return;
        try {
            await api.delete(`/assets/${id}`);
            toast.success("Archived");
            window.history.back();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const addService = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const body = { ...f };
            if (body.cost === "") delete body.cost; else body.cost = Number(body.cost);
            await api.post(`/assets/${id}/service`, body);
            toast.success("Service entry logged");
            setOpen(false);
            setF({ date: new Date().toISOString().slice(0, 10), vendor: "", cost: "", notes: "" });
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    if (!asset) return <div className="text-stone-500 text-sm">Loading…</div>;
    const card = related?.card;

    return (
        <div data-testid="asset-detail-page">
            <Link to="/assets" className="inline-flex items-center gap-1.5 text-stone-500 hover:text-stone-900 text-[12px] mb-4">
                <ArrowLeft size={13} /> All assets
            </Link>
            <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><Package size={12} /> {asset.category}</div>
                    <h1 className="font-display text-3xl sm:text-4xl text-stone-900 leading-none mt-2">{asset.name}</h1>
                    <div className="text-sm text-stone-500 mt-2">
                        {[asset.brand, asset.model].filter(Boolean).join(" · ") || "—"}
                        {asset.serial_number && <> · SN <span className="font-mono-figure">{asset.serial_number}</span></>}
                    </div>
                </div>
                <div className="flex gap-2">
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button variant="outline" data-testid="asset-service-btn"><Wrench size={13} className="mr-1.5" /> Log service</Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl">Service entry</DialogTitle>
                                <DialogDescription className="text-[13px] text-stone-500">Log a repair, cleaning, or check-up.</DialogDescription>
                            </DialogHeader>
                            <form onSubmit={addService} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label className="eyebrow">Date</Label>
                                        <Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="mt-1.5" /></div>
                                    <div><Label className="eyebrow">Vendor</Label>
                                        <Input value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} className="mt-1.5" placeholder="Apple Service Center" /></div>
                                    <div><Label className="eyebrow">Cost</Label>
                                        <Input type="number" step="0.01" value={f.cost} onChange={(e) => setF({ ...f, cost: e.target.value })} className="mt-1.5 font-mono-figure" /></div>
                                </div>
                                <div><Label className="eyebrow">Notes</Label>
                                    <Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1.5" /></div>
                                <Button type="submit" disabled={busy} className="w-full bg-stone-900 hover:bg-stone-800 text-white">{busy ? "Saving…" : "Log entry"}</Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                    <Button variant="ghost" onClick={remove} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash2 size={13} className="mr-1.5" /> Archive
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                    <div className="rounded-lg border border-stone-200 bg-white p-5">
                        <div className="eyebrow mb-2">Provenance</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                            <InfoRow label="Purchase date" value={fmtDate(asset.purchase_date)} />
                            <InfoRow label="Purchase price" value={fmtMoney(asset.purchase_price, asset.currency)} />
                            <InfoRow label="Current value" value={fmtMoney(asset.current_value, asset.currency)} />
                            <InfoRow label="Vendor" value={asset.vendor} />
                            <InfoRow label="Invoice number" value={asset.invoice_number} />
                            <InfoRow label="Owner" value={asset.owner} />
                            <InfoRow label="Location" value={asset.location ? <Sensitive level="highly_sensitive" as="span">{asset.location}</Sensitive> : null} />
                            <InfoRow label="Condition" value={asset.condition} />
                        </div>
                    </div>

                    <div className="rounded-lg border border-stone-200 bg-white p-5">
                        <div className="eyebrow mb-2 flex items-center gap-1.5"><ShieldCheck size={12} /> Warranty &amp; Insurance</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                            <InfoRow label="Warranty ends" value={fmtDate(asset.warranty_end_date)} />
                            <InfoRow label="AppleCare / extended ends" value={fmtDate(asset.applecare_end_date)} />
                            <InfoRow label="Insurance provider" value={asset.insurance_provider} />
                            <InfoRow label="Insurance policy" value={asset.insurance_policy_no} />
                            <InfoRow label="Insurance ends" value={fmtDate(asset.insurance_end_date)} />
                        </div>
                        <div className="text-[11px] text-stone-500 mt-3">
                            These expiries appear automatically on the Renewals tracker with 30 / 7 / 1-day reminders.
                        </div>
                    </div>

                    <div className="rounded-lg border border-stone-200 bg-white p-5">
                        <div className="eyebrow mb-2 flex items-center gap-1.5"><Wrench size={12} /> Service history</div>
                        {(asset.service_history || []).length === 0 ? (
                            <div className="text-sm text-stone-400 py-6 text-center">No service entries yet.</div>
                        ) : (
                            <ul className="divide-y divide-stone-100">
                                {(asset.service_history || []).slice().reverse().map((s, i) => (
                                    <li key={i} className="py-2 flex items-start gap-3">
                                        <CalIcon size={12} className="text-stone-400 mt-1" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-stone-900">{s.vendor || "Service"}</div>
                                            <div className="text-[12px] text-stone-500">{fmtDate(s.date)}{s.notes ? <> · {s.notes}</> : null}</div>
                                        </div>
                                        {s.cost != null && <div className="font-mono-figure text-sm text-stone-900">{fmtMoney(s.cost, s.currency)}</div>}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                <div className="space-y-4">
                    {card && (
                        <div className="rounded-lg border border-stone-200 bg-white p-5">
                            <div className="eyebrow mb-2 flex items-center gap-1.5"><CreditCard size={12} /> Bought on</div>
                            <Link to={`/cards/${card.id}`} className="block hover:opacity-80 transition-opacity">
                                <div className="text-sm font-medium text-stone-900">{card.bank_name}</div>
                                <div className="text-[13px] text-stone-500">{card.card_name} · •••• {card.last_4}</div>
                            </Link>
                        </div>
                    )}
                    <div className="rounded-lg border border-stone-200 bg-white p-5">
                        <div className="eyebrow mb-2">Notes</div>
                        <div className="text-sm text-stone-700 whitespace-pre-wrap">{asset.notes || <span className="text-stone-400">—</span>}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
